import type { IStoreApiResponse, IStoreResponse } from '@/common/interfaces/store.interface';

type IStorePayload = IStoreResponse & {
  order_items?: IStoreResponse['orderItems'];
  orderItems?: IStoreResponse['orderItems'];
  designGroups?: IStoreResponse['design_groups'];
  orderFlags?: IStoreResponse['order_flags'];
};

const isStorePayload = (value: unknown): value is IStorePayload => {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  const { id } = value as IStorePayload;
  return typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id));
};

const resolveOrderItems = (
  store: IStorePayload,
  fallback?: IStoreResponse['orderItems']
): IStoreResponse['orderItems'] => store.orderItems ?? store.order_items ?? fallback ?? [];

const buildNormalizedStore = (
  store: IStorePayload,
  fallbackOrderItems?: IStoreResponse['orderItems']
): IStoreResponse => ({
  ...store,
  orderItems: resolveOrderItems(store, fallbackOrderItems),
  design_groups: store.design_groups ?? store.designGroups ?? [],
  order_flags: store.order_flags ?? store.orderFlags ?? [],
});

/** Normalize store GET payload (wrapped, unwrapped, snake_case / alternate keys). */
export const normalizeStoreApiResponse = (
  response: IStoreApiResponse | Record<string, unknown> | undefined
): IStoreApiResponse | undefined => {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const raw = response as IStoreApiResponse & {
    data?: IStorePayload | IStoreApiResponse['data'];
    order_items?: IStoreResponse['orderItems'];
  };

  const nestedData = raw.data;
  const nestedStore = isStorePayload(nestedData) ? nestedData : undefined;
  const topLevelStore = nestedStore ?? (isStorePayload(raw) ? (raw as IStorePayload) : undefined);

  if (!topLevelStore) {
    return raw.data ? (raw as IStoreApiResponse) : undefined;
  }

  const topLevelOrderItems = Array.isArray(raw.order_items) ? raw.order_items : undefined;

  return {
    status: typeof raw.status === 'string' ? raw.status : 'success',
    data: buildNormalizedStore(topLevelStore, topLevelOrderItems),
  };
};

export const resolveStoreDetailsFromQueryResult = (
  queryData: unknown,
  queryError?: unknown
): IStoreResponse | undefined => {
  const candidates = [queryData, (queryError as { data?: unknown } | undefined)?.data];
  const normalizedStore = candidates
    .map((candidate) => normalizeStoreApiResponse(candidate as IStoreApiResponse))
    .find((normalized) => normalized?.data);

  return normalizedStore?.data;
};
